import { LocalStorageService } from '../../../services/local-storage.service';
import { DynamicWorkflowNode, DynamicWorkflowTransition } from './dynamic-framework.models';

/** The node card is a fixed size, so edges can be aimed without measuring. */
export const NODE_WIDTH = 204;
export const NODE_HEIGHT = 78;
export const GRID = 16;

/** Auto-layout steps: one node plus a gap wide enough for a labelled edge. */
const COLUMN_STEP = 272;
const ROW_STEP = 112;
const ORIGIN_X = 48;
const ORIGIN_Y = 32;

export interface NodePosition {
  x: number;
  y: number;
}

export type WorkflowLayout = Record<string, NodePosition>;

/**
 * Where a node sits on the canvas.
 *
 * The browser owns this, not the server: a node row stores a display order and
 * has no coordinate columns, and adding them is a schema change that belongs in
 * its own migration. Until then a layout is a per-browser preference, the same
 * arrangement the data table already uses for column widths - the graph itself
 * (nodes, transitions, which one starts) is server state and unaffected. A
 * second browser sees the auto-layout, which is derived from that graph and so
 * is never wrong, only unarranged.
 *
 * LocalStorageService scopes the key to the current company and user.
 * Callers supply only the workflow key; account names and login generations
 * are not part of the workflow's own key.
 */
const STORAGE_PREFIX = 'br.leads-v2.workflow-layout.';

export function readLayout(storage: LocalStorageService, storageKey: string): WorkflowLayout {
  if (!storageKey) return {};
  try {
    const parsed = storage.get<unknown>(STORAGE_PREFIX + storageKey);
    if (!parsed || typeof parsed !== 'object') return {};
    const layout: WorkflowLayout = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      const point = value as Partial<NodePosition> | null;
      if (typeof point?.x === 'number' && typeof point?.y === 'number') {
        layout[key] = { x: point.x, y: point.y };
      }
    });
    return layout;
  } catch {
    return {};
  }
}

export function writeLayout(storage: LocalStorageService, storageKey: string, layout: WorkflowLayout): void {
  if (!storageKey) return;
  storage.set(STORAGE_PREFIX + storageKey, layout);
}

/** Carries a layout drawn before the workflow had a key over to its real one. */
export function renameLayout(storage: LocalStorageService, fromKey: string, toKey: string): void {
  if (!fromKey || !toKey) return;
  const layout = readLayout(storage, fromKey);
  if (!Object.keys(layout).length) return;
  if (!storage.set(STORAGE_PREFIX + toKey, layout)) return;
  try {
    storage.removeItem(STORAGE_PREFIX + fromKey);
  } catch {
    // Losing the old copy is harmless; the new one is already written.
  }
}

/** Grid alignment only; workflow world coordinates are deliberately unbounded. */
export function snap(value: number): number {
  return Math.round(value / GRID) * GRID;
}

/**
 * Arranges the graph left to right from nodes with no incoming transition.
 *
 * Depth is the longest path in, not the shortest, so a node never sits to the
 * left of something that feeds it. Nodes no edge reaches - a fresh node, or an
 * island the user has not connected yet - are placed in columns of their own
 * after the rest rather than stacked on the origin.
 */
export function autoLayout(
  nodes: DynamicWorkflowNode[],
  transitions: DynamicWorkflowTransition[],
): WorkflowLayout {
  const ordered = [...nodes].sort((a, b) => a.displayOrder - b.displayOrder);
  if (!ordered.length) return {};

  const keys = new Set(ordered.map(node => node.key));
  const outgoing = new Map(ordered.map(node => [node.key, [] as string[]]));
  const incoming = new Map(ordered.map(node => [node.key, 0]));
  transitions
    .filter(edge => keys.has(edge.fromNodeKey) && keys.has(edge.toNodeKey))
    .forEach(edge => {
      outgoing.get(edge.fromNodeKey)?.push(edge.toNodeKey);
      incoming.set(edge.toNodeKey, (incoming.get(edge.toNodeKey) ?? 0) + 1);
    });

  const depth = new Map<string, number>();
  const pending = new Map(incoming);
  const queue = ordered
    .filter(node => incoming.get(node.key) === 0)
    .map(node => node.key);
  queue.forEach(key => depth.set(key, 0));

  while (queue.length) {
    const fromKey = queue.shift()!;
    const fromDepth = depth.get(fromKey) ?? 0;
    outgoing.get(fromKey)?.forEach(toKey => {
      depth.set(toKey, Math.max(depth.get(toKey) ?? 0, fromDepth + 1));
      const left = (pending.get(toKey) ?? 1) - 1;
      pending.set(toKey, left);
      if (left === 0) queue.push(toKey);
    });
  }

  // A cycle leaves nodes without a depth; give each its own column after the
  // deepest one that was resolved, so nothing lands on top of anything else.
  let spare = depth.size ? Math.max(...depth.values()) : -1;
  ordered.forEach(node => {
    if (!depth.has(node.key)) depth.set(node.key, ++spare);
  });

  const filled = new Map<number, number>();
  const layout: WorkflowLayout = {};
  ordered.forEach(node => {
    const column = depth.get(node.key) ?? 0;
    const row = filled.get(column) ?? 0;
    filled.set(column, row + 1);
    layout[node.key] = {
      x: ORIGIN_X + column * COLUMN_STEP,
      y: ORIGIN_Y + row * ROW_STEP,
    };
  });
  return layout;
}

/** Where a newly added node lands, stepped so a run of them does not stack. */
export function placeNewNode(existingCount: number): NodePosition {
  return { x: snap(96), y: snap(120 + existingCount * 12) };
}
